import { useFilters } from "@fluidattacks/design";
import type {
  IFilterOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IVulnRowAttr } from "features/vulnerabilities/types";
import { VulnerabilitySourceType } from "gql/graphql";
import type {
  GetMeVulnerabilityDraftsQueryVariables,
  VulnerabilityState,
} from "gql/graphql";
import { useFiltersStorage } from "storage/filters";
import {
  getCheckedFilter,
  getDateRangeFilter,
  getNewFilter,
  getNumberRangeFilter,
} from "utils/filters";

const useVulnerabilityDraftsFilters = ({
  setFilters,
}: {
  setFilters: (filters: GetMeVulnerabilityDraftsQueryVariables) => void;
}): IUseFilterProps<IVulnRowAttr> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const options: IOptionsProps<IVulnRowAttr>[] = useMemo(
    (): IOptionsProps<IVulnRowAttr>[] => [
      {
        filterOptions: [
          { key: "organizationName", label: "Organization", type: "text" },
        ],
        label: t("Organization"),
      },
      {
        filterOptions: [
          {
            key: "groupName",
            label: t("organization.tabs.groups.newGroup.name.text"),
            type: "text",
          },
        ],
        label: t("organization.tabs.groups.newGroup.name.text"),
      },
      {
        filterOptions: [
          {
            filterFn: "includesInsensitive",
            key: "finding",
            label: t(
              "searchFindings.tabVuln.vulnTable.vulnerabilityType.title",
            ),
            type: "text",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.vulnerabilityType.title"),
      },
      {
        filterOptions: [
          {
            filterFn: "includesInsensitive",
            key: "hacker",
            label: t("searchFindings.tabVuln.vulnTable.hacker"),
            type: "text",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.hacker"),
      },
      {
        filterOptions: [
          {
            key: "sourceType",
            label: t("searchFindings.tabVuln.vulnTable.source"),
            options: [
              {
                label: t(
                  "searchFindings.tabVuln.vulnTable.vulnerabilitySource.MACHINE",
                ),
                value: "Machine",
              },
              {
                label: t(
                  "searchFindings.tabVuln.vulnTable.vulnerabilitySource.NON-MACHINE",
                ),
                value: "Non-Machine",
              },
            ],
            type: "select",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.source"),
      },
      {
        filterOptions: [
          {
            key: "state",
            label: t("searchFindings.tabVuln.vulnTable.status"),
            options: [
              {
                label: t("searchFindings.tabVuln.rejected"),
                value: "REJECTED",
              },
              {
                checked: true,
                label: t("searchFindings.tabVuln.submitted"),
                value: "SUBMITTED",
              },
            ],
            type: "radioButton",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.status"),
      },
      {
        filterOptions: [
          {
            key: "reportDate",
            label: t("searchFindings.tabVuln.vulnTable.reportDate"),
            type: "dateRange",
          },
        ],
        label: t("searchFindings.tabVuln.vulnTable.reportDate"),
      },
      {
        filterOptions: [
          {
            key: "severityThreatScore",
            label: t("searchFindings.tabDescription.severity"),
            type: "numberRange",
          },
        ],
        label: t("searchFindings.tabDescription.severity"),
      },
    ],
    [t],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("vulns_drafts") as string | null;
  }, [getValues]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("vulns_drafts", value);
    },
    [setValues],
  );

  const handleFiltersChange = useCallback(
    (newFilters: readonly IFilterOptions<IVulnRowAttr>[]): void => {
      const newFiltersArray = newFilters as IFilterOptions<IVulnRowAttr>[];
      const organizationName = getNewFilter(
        newFiltersArray,
        "organizationName",
      )?.value;
      const groupName = getNewFilter(newFiltersArray, "groupName")?.value;
      const findingTitle = getNewFilter(newFiltersArray, "finding")?.value;
      const hacker = getNewFilter(newFiltersArray, "hacker")?.value;
      const sourceType = getNewFilter(newFiltersArray, "sourceType")?.value;
      const state = getCheckedFilter(newFiltersArray, "state")
        ?.value as VulnerabilityState;
      const reportDateRange = getDateRangeFilter(newFiltersArray, "reportDate");
      const severityRange = getNumberRangeFilter(
        newFiltersArray,
        "severityThreatScore",
      );

      setFilters({
        findingTitle: findingTitle?.toString(),
        fromReportDate: reportDateRange.from,
        groupName: groupName?.toString(),
        hacker: hacker?.toString(),
        maxSeverityTemporalScore: severityRange.max,
        minSeverityTemporalScore: severityRange.min,
        organizationName: organizationName?.toString(),
        sourceType:
          sourceType === "Non-Machine"
            ? VulnerabilitySourceType.Squad
            : (sourceType?.toUpperCase() as VulnerabilitySourceType),
        state,
        toReportDate: reportDateRange.to,
      });
    },
    [setFilters],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFiltersChange,
    options,
    setValues: handleSetValues,
  });
};

export { useVulnerabilityDraftsFilters };
